import { useState, type InputHTMLAttributes, type ReactNode } from 'react';
import BrandMark from './BrandMark.js';
import signupVideo from '../assets/signup.mp4';
import signupSvg from '../assets/signup.svg';

/**
 * GİRİŞ KABUĞU — masaüstündeki referans DXP admin panelinin auth ekranlarıyla
 * BİREBİR aynı görsel dil (AuthLayout + AuthHeroPanel + AnimateIn + form parçaları):
 *
 *   · beyaz tam sayfa zemin; üstte marka (BrandMark + Gloock "DXP") ve sağda
 *     sayfaya göre değişen bir eylem yuvası ("Hesabınız var mı? Giriş yapın" hapı gibi),
 *   · gövde iki sütun (`lg:grid-cols-2`): SOLDA kartsız/çıplak form sütunu
 *     (referans `AuthCard` 274px), SAĞDA kare hero paneli — signup.svg taban katmanı,
 *     üstünde %40 opaklıkta taşan (-27px inset) döngülü video ve altta karartma şeridi,
 *   · hero `lg` altında tamamen gizlenir; video yüklenemezse marka gradyanına düşer,
 *   · içerik `AnimateIn` ile kademeli girer (0/60/120/160 ms).
 *
 * NEDEN TEK BİLEŞEN: giriş · kayıt · doğrulama · davet · parola sıfırlama arka
 * arkaya görülür ve TEK ürün gibi durmalıdır. Faz 7 öncesinde her sayfa kendi
 * kabuğunu kopyalıyordu ve üçü şimdiden ayrışmıştı — kopya, ayrışmanın kendisidir.
 *
 * DAVRANIŞ DEĞİŞMEZİ: bu dosya YALNIZ görsel dildir. Sayfaların tüm mantığı,
 * `data-testid`leri ve kullanıcıya giden metinleri OLDUĞU GİBİ kalır — e2e/DOM
 * paketleri onlara kilitlidir.
 */

/** Referans `AuthHeroPanel` fallback'ı — video hatasında marka gradyanı. */
const GRADYAN_YEDEK = [
  'radial-gradient(ellipse 80% 80% at 15% 15%, #f56600 0%, transparent 55%)',
  'radial-gradient(ellipse 65% 65% at 85% 85%, #3b82f6 0%, transparent 55%)',
  'radial-gradient(ellipse 55% 55% at 75% 25%, #a5b4fc 0%, transparent 50%)',
  '#f2d5c4',
].join(', ');

/** Referans `AnimateIn` — kademeli kart girişi (animasyonlar index.css'te). */
export function AnimateIn({
  delay = 0,
  fade = false,
  className = '',
  children,
}: {
  delay?: number;
  fade?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`${fade ? 'animate-auth-fade' : 'animate-auth-item'} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export interface GirisKabuguProps {
  /** Sayfanın kök `data-testid`i — e2e sözleşmesi (giris-sayfasi, kayit-sayfasi…). */
  testId: string;
  /** Üst çubuğun sağ yuvası (referansta "Don't have an account? Sign up"). */
  ustSag?: ReactNode;
  /**
   * Referans tasarım sisteminde İKİ auth düzeni vardır:
   *   · `hero`   — kayıt akışı: solda form, sağda kare hero paneli (AuthLayout),
   *   · `ortala` — giriş ve parola akışları: form sayfanın ORTASINDA, arkasında
   *     bulanık marka-gradyanı lekesi (login/reset-password ekranları).
   * Varsayılan `hero`dur; sayfa hangi akışa aitse onu seçer.
   */
  duzen?: 'hero' | 'ortala';
  children: ReactNode;
}

/**
 * Sağ sütun — referans `AuthHeroPanel`: kare panelde signup.svg taban, üstünde
 * -27px taşan %40 opak video, en altta karartma + tek satırlık ürün cümlesi.
 * (Referanstaki "Trusted by 3,700 Companies" bir maket metniydi; burada uydurma
 * bir sayı yerine ürünün gerçek tanımı yazılır.)
 */
function HeroPanel() {
  const [videoHata, setVideoHata] = useState(false);

  return (
    <div className="relative w-full max-w-[634px]" style={{ aspectRatio: '1 / 1' }}>
      {/* Taban katman: signup.svg */}
      <img
        src={signupSvg}
        alt=""
        className="absolute inset-0 z-0 h-full w-full rounded-2xl object-contain"
      />

      {/* SVG üstünde düşük opaklıkta video */}
      <div
        className="absolute z-10 overflow-hidden rounded-3xl opacity-40"
        style={{ inset: '-27px' }}
      >
        {!videoHata ? (
          <video
            className="h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onError={() => setVideoHata(true)}
          >
            <source src={signupVideo} type="video/mp4" />
          </video>
        ) : (
          <div className="h-full w-full" style={{ background: GRADYAN_YEDEK }} />
        )}
      </div>

      {/* Karartma şeridi */}
      <div className="absolute inset-x-0 bottom-0 z-20 h-24 rounded-b-2xl bg-gradient-to-t from-black/30 to-transparent" />

      {/* Alt etiket */}
      <p className="absolute bottom-8 left-0 right-0 z-20 text-center font-grotesk text-[14px] font-semibold tracking-wide text-white">
        Dijital deneyim analitiği + otomatik içgörü
      </p>
    </div>
  );
}

export default function GirisKabugu({
  testId,
  ustSag,
  duzen = 'hero',
  children,
}: GirisKabuguProps) {
  return (
    <div
      data-testid={testId}
      className={`flex min-h-screen w-full flex-col ${duzen === 'ortala' ? 'bg-[#faf9f7]' : 'bg-white'}`}
    >
      {/* Üst çubuk — referans AuthLayout header'ı */}
      <header className="flex shrink-0 items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <BrandMark size={23} />
          <span className="font-gloock text-[20px] text-ink">DXP</span>
        </div>
        {ustSag !== undefined ? <div className="flex items-center gap-3">{ustSag}</div> : null}
      </header>

      {duzen === 'hero' ? (
        /* İki sütun: solda çıplak form, sağda hero (lg altında gizli) */
        <div className="grid flex-1 grid-cols-1 gap-0 lg:grid-cols-2">
          <div className="flex items-center justify-center px-8 py-12">
            {/* Referans AuthCard sütunu: 274px dış, 8px iç boşluk → 258px alanlar */}
            <div className="w-[274px] max-w-full p-2">{children}</div>
          </div>
          <div className="hidden items-center justify-center p-4 lg:flex">
            <HeroPanel />
          </div>
        </div>
      ) : (
        /*
         * Ortalanmış düzen — referans login/reset ekranları: geniş (520px), ince
         * çerçeveli, YARI SAYDAM bir kart; marka-gradyanı lekesi karta sarılıdır ve
         * kartın İÇİNDEN görünür (buzlu etki: `bg-white/55` + `backdrop-blur`).
         * Leke iki parçadır — üstte turuncu, altta mavi-mor — ve kartın alt/üst
         * kenarından hafifçe taşar. Saf süstür: `aria-hidden` + `pointer-events-none`.
         */
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <div className="relative w-[520px] max-w-full">
            <div aria-hidden className="pointer-events-none absolute inset-0">
              <div
                className="absolute -top-14 left-1/2 h-[300px] w-[280px] -translate-x-[62%] rounded-full opacity-70 blur-2xl"
                style={{ background: 'radial-gradient(closest-side, rgba(245,102,0,0.45), transparent)' }}
              />
              <div
                className="absolute -bottom-16 left-1/2 h-[320px] w-[300px] -translate-x-[32%] rounded-full opacity-70 blur-2xl"
                style={{ background: 'radial-gradient(closest-side, rgba(99,102,241,0.4), transparent)' }}
              />
            </div>
            <div className="relative z-10 rounded-2xl border border-[#e5e5e5]/90 bg-white/55 px-9 py-9 shadow-sm backdrop-blur-[3px]">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Form parçaları — referans admin panelinin alan dili                          */
/* -------------------------------------------------------------------------- */

/** Kart başlığı: 24px grotesk medium (referans `h1`). */
export function KartBaslik({ children }: { children: ReactNode }) {
  return (
    <h1 className="font-grotesk text-[24px] font-medium leading-tight text-ink">{children}</h1>
  );
}

/** Başlık altı kısa açıklama. */
export function KartAciklama({ children }: { children: ReactNode }) {
  return <p className="font-grotesk text-[12px] font-normal text-[#686868]">{children}</p>;
}

export function AlanEtiketi({ children }: { children: ReactNode }) {
  return (
    <p className="font-grotesk text-[12px] font-normal leading-[13px] text-ink">{children}</p>
  );
}

/** Metin alanı — 40px, `#e5e5e5` çerçeve, odakta marka turuncusu. */
export function MetinAlani(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="flex h-10 w-full items-center rounded-lg border border-[#e5e5e5] bg-white px-3 transition-colors duration-150 focus-within:border-brand">
      <input
        {...props}
        className="flex-1 bg-transparent font-grotesk text-[12px] font-normal text-ink outline-none placeholder:text-[#a5a5a5] focus-visible:ring-0 focus-visible:ring-offset-0"
      />
    </div>
  );
}

/**
 * Parola alanı — göz simgesiyle göster/gizle (referans `PasswordInput`).
 *
 * Göster/gizle durumu BİLEŞENİN İÇİNDEDİR: sayfa mantığına tek bir state sızdırmaz
 * ve `data-testid` doğrudan input'a gider — testler `fill` etmeye devam eder.
 */
export function ParolaAlani(props: InputHTMLAttributes<HTMLInputElement>) {
  const [goster, setGoster] = useState(false);
  return (
    <div className="flex h-10 w-full items-center gap-2 rounded-lg border border-[#e5e5e5] bg-white px-3 transition-colors duration-150 focus-within:border-brand">
      <input
        {...props}
        type={goster ? 'text' : 'password'}
        className="flex-1 bg-transparent font-grotesk text-[12px] font-normal text-ink outline-none placeholder:text-[#a5a5a5] focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      <button
        type="button"
        onClick={() => setGoster((v) => !v)}
        className="shrink-0 text-[#686868] transition-colors hover:text-ink"
        aria-label={goster ? 'Parolayı gizle' : 'Parolayı göster'}
      >
        {goster ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

/**
 * Parola güç göstergesi — referans set-password ekranındaki renkli barlar.
 *
 * YALNIZ GERİ BİLDİRİMDİR: parolayı kabul/ret kararı §27.3 politikasıyla sunucuda
 * (ve `passwordProblem` ön kontrolüyle) verilir; bu barlar o karara katılmaz.
 * Boş parolada hiç çizilmez — kullanıcı daha yazmadan "Zayıf" damgası yemez.
 */
export function ParolaGucu({ parola }: { parola: string }) {
  if (parola === '') return null;

  /*
   * Puanlama bilinçli olarak kabadır (3 basamak): 1 = politika altı, 2 = politikayı
   * karşılıyor (≥10 + harf + rakam), 3 = üstüne uzunluk ya da karakter çeşidi koymuş.
   * İnce taneli "entropi skoru" kullanıcıya yanlış bir kesinlik duygusu verirdi.
   */
  const politikayiKarsilar = parola.length >= 10 && /[a-zA-Z]/.test(parola) && /\d/.test(parola);
  const guclu =
    politikayiKarsilar &&
    (parola.length >= 14 || (/[A-Z]/.test(parola) && /[a-z]/.test(parola) && /[^a-zA-Z0-9]/.test(parola)));
  const puan = guclu ? 3 : politikayiKarsilar ? 2 : 1;

  const etiket = puan === 3 ? 'Güçlü' : puan === 2 ? 'Orta' : 'Zayıf';
  const renk = puan === 3 ? 'bg-emerald-500' : puan === 2 ? 'bg-amber-500' : 'bg-red-500';
  const metinRengi =
    puan === 3 ? 'text-emerald-600' : puan === 2 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="flex items-center gap-2" data-testid="parola-guc">
      <div className="flex flex-1 gap-1">
        {[1, 2, 3].map((n) => (
          <span
            key={n}
            className={`h-1 flex-1 rounded-full transition-colors duration-200 ${
              n <= puan ? renk : 'bg-[#e5e5e5]'
            }`}
          />
        ))}
      </div>
      <span className={`font-grotesk text-[11px] font-medium ${metinRengi}`}>{etiket}</span>
    </div>
  );
}

/** Birincil (marka) buton — 40px, hover/active koyulaşma (referans `Continue`). */
export function MarkaButonu({
  children,
  ...props
}: InputHTMLAttributes<HTMLButtonElement> & {
  type?: 'submit' | 'button';
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      {...props}
      className="h-10 w-full rounded-lg border border-brand bg-brand font-grotesk text-[12px] font-medium text-white transition-colors duration-150 hover:bg-[#d95b00] active:bg-[#c25200] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/** İkincil buton/bağlantı görünümü — beyaz zemin, gri çerçeve. */
export const IKINCIL_BUTON =
  'flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-[#e5e5e5] bg-white font-grotesk text-[12px] font-medium text-ink transition-colors duration-150 hover:border-[#c8c8c8]';

/** Üst çubuk sağındaki hap bağlantı (referans "Sign up"). */
export const UST_HAP =
  'rounded-md border border-brand px-4 py-1.5 font-grotesk text-[13px] text-brand transition-colors hover:bg-brand/5';

/**
 * Hata şeridi — kırmızı zeminli banner (referans error banner).
 *
 * `data-testid` ve `role="alert"` İÇTEKİ metin öğesindedir: testler metni testid
 * üzerinden okur, banner süslemesi sözleşmeye girmez.
 */
export function HataSeridi({ testId, children }: { testId?: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
      <svg className="h-4 w-4 shrink-0 text-red-500" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      <p
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
        role="alert"
        className="text-[12px] font-medium text-red-600"
      >
        {children}
      </p>
    </div>
  );
}
